import React, { useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { EstadoCarregando, EstadoErro, EstadoVazio } from "./Estados";

/** Item exibido no ranking; chave estável vinda dos ids da API. */
export interface IItemRanking {
  chave: string;
  nome: string;
  quantidade: number;
}

// A lista abre em etapas: Top 3 → Top 10 → todos.
const TOP_PADRAO = 3;
const TOP_EXPANDIDO = 10;

interface IListaRanking {
  titulo: string;
  icone: string;
  cor: string;
  descricaoRotulo: string;
  legendaFiltro: string;
  carregando: boolean;
  erro: string | null;
  itens: IItemRanking[] | null;
}

/**
 * A API ordena por quantidade com desempate por id.
 * Na tela o desempate é por nome, apenas para exibição (sem reagrupar nem somar).
 */
const ordenarComDesempate = (itens: IItemRanking[]) =>
  itens
    .map((item, indice) => ({ item, indice }))
    .sort(
      (a, b) =>
        b.item.quantidade - a.item.quantidade ||
        a.item.nome.localeCompare(b.item.nome, "pt-BR") ||
        a.indice - b.indice,
    )
    .map(({ item }) => item);

/** Posição com empate compartilhado (1, 1, 3...). */
const calcularPosicoes = (itens: IItemRanking[]) =>
  itens.map((item, indice) => {
    let primeiro = indice;
    while (primeiro > 0 && itens[primeiro - 1].quantidade === item.quantidade) primeiro--;
    return primeiro + 1;
  });

const ListaRanking: React.FC<IListaRanking> = ({
  titulo,
  icone,
  cor,
  descricaoRotulo,
  legendaFiltro,
  carregando,
  erro,
  itens,
}) => {
  // null = todos
  const [limite, setLimite] = useState<number | null>(TOP_PADRAO);

  const ordenados = useMemo(() => ordenarComDesempate(itens ?? []), [itens]);
  const posicoes = useMemo(() => calcularPosicoes(ordenados), [ordenados]);
  const maior = ordenados[0]?.quantidade ?? 0;
  const visiveis = limite === null ? ordenados : ordenados.slice(0, limite);
  const temMais = visiveis.length < ordenados.length;
  // Próxima etapa: Top 10 só se ainda sobrar gente depois dele.
  const proximoLimite =
    limite === TOP_PADRAO && ordenados.length > TOP_EXPANDIDO ? TOP_EXPANDIDO : null;
  const mostrandoTop = limite !== null && temMais;

  const renderizarConteudo = () => {
    if (carregando) return <EstadoCarregando texto="Carregando ranking..." />;
    if (erro) return <EstadoErro mensagem={erro} />;
    if (!ordenados.length) {
      return <EstadoVazio texto="Sem feedbacks no período e tipo selecionados." />;
    }

    return (
      <>
        <ol className="list-unstyled mb-0">
          {visiveis.map(({ chave, nome, quantidade }, indice) => (
            <li key={chave} className="d-flex align-items-center py-2 border-bottom">
              <span
                className={`fw-bold text-end me-3 ${posicoes[indice] <= 3 ? `text-${cor}` : "text-muted"}`}
                style={{ minWidth: "2.5rem" }}
              >
                {posicoes[indice]}º
              </span>
              <div className="flex-grow-1 overflow-hidden">
                <div className="d-flex justify-content-between">
                  <span className="text-truncate me-2" title={nome}>
                    {nome}
                  </span>
                  <span className="fw-bold">{quantidade}</span>
                </div>
                <div className="progress mt-1" style={{ height: "6px" }}>
                  <div
                    className={`progress-bar bg-${cor}`}
                    role="progressbar"
                    style={{ width: `${maior ? (quantidade / maior) * 100 : 0}%` }}
                    aria-valuenow={quantidade}
                    aria-valuemin={0}
                    aria-valuemax={maior}
                  />
                </div>
              </div>
            </li>
          ))}
        </ol>

        {ordenados.length > TOP_PADRAO && (
          <div className="text-center mt-3">
            {temMais && (
              <Button variant="link" size="sm" onClick={() => setLimite(proximoLimite)}>
                {proximoLimite ? `Ver Top ${proximoLimite}` : `Ver todos (${ordenados.length})`}
              </Button>
            )}
            {limite !== TOP_PADRAO && (
              <Button variant="link" size="sm" onClick={() => setLimite(TOP_PADRAO)}>
                Mostrar só o Top {TOP_PADRAO}
              </Button>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <Card className="h-100 border-0 shadow-sm">
      <Card.Header className="bg-transparent">
        <h5 className="mb-1">
          <i className={`${icone} text-${cor} me-2`}></i>
          {mostrandoTop ? `Top ${limite} · ${titulo}` : titulo}
        </h5>
        <small className="text-muted d-block">{legendaFiltro}</small>
        <small className="text-muted d-block">{descricaoRotulo}</small>
      </Card.Header>
      <Card.Body>{renderizarConteudo()}</Card.Body>
    </Card>
  );
};

export default ListaRanking;
